import type { ReactNode } from 'react';

// A card's number, with how far along that card is drawn around it.
//
// The disc keeps its own colours — in the reader they say what the phase is DOING
// (running, queued, idle), in a section switcher they say nothing at all — and the ring
// says how much of the work is behind you. Two facts, two marks, one badge.
//
// Nothing fills the disc for being FINISHED. A closed ring is what finished looks like,
// and it is the same object as a half-closed one; a solid green disc beside a ringed grey
// one was two different marks for two readings of one measure.
//
// It repeats the bar under it, deliberately: the number is where the eye lands first, and
// a ring there answers "how far along" without moving down to the bar. The bar keeps the
// precision — an arc cannot tell 5% from 15%, and a wide track can.
//
// Emerald 500 written out, because a conic gradient takes colours and not class names. The
// track is `currentColor`, so it comes from a Tailwind text class on the wrapper and gets
// its dark-mode variant the ordinary way; the disc sets its own text colour, so nothing
// else inherits it.
export function ProgressBadge({ fraction, busy, badgeClass, children }: {
  // 0..1, or undefined for a badge with no progress to report (the Admin console, the
  // catalog) — which draws the bare disc, exactly as before.
  fraction?: number;
  // THE WORK IS RUNNING: the ring becomes a spinner — a green arc going round the track, the way a
  // loader does — in place of what the last run finished, which beside it said two things at once.
  busy?: boolean;
  // The disc's own classes: its background, its text colour, any hatching.
  badgeClass: string;
  children: ReactNode;
}) {
  const disc = (
    <span
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-[color,background-color] ${badgeClass}`}
    >
      {children}
    </span>
  );
  if (fraction == null) return disc;
  // A sliver at anything above zero, the same floor the bar uses, so "barely started"
  // reads as itself rather than as nothing. The ring is drawn even at zero: it is what
  // keeps every card the same size.
  const pct = Math.min(100, Math.max(fraction > 0 ? 4 : 0, fraction * 100));
  if (busy) {
    return (
      // THE ARC SPINS AND THE NUMBER DOES NOT: the ring is a layer of its own under the disc, turned
      // by `animate-spin` (still, where motion is reduced: a third of a ring then says "running").
      <span className="relative inline-flex shrink-0 rounded-full p-[2px] text-slate-200">
        <span
          aria-hidden
          className="absolute inset-0 rounded-full motion-safe:animate-spin"
          style={{ background: 'conic-gradient(#10b981 0 30%, currentColor 0)' }}
        />
        <span className="relative">{disc}</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 rounded-full p-[2px] text-slate-200"
      style={{ background: `conic-gradient(#10b981 ${pct}%, currentColor 0)` }}
    >
      {disc}
    </span>
  );
}
